import { userHasPermission, type UserPublic } from "@eduviaos/shared";

/**
 * Phase H8 — pure helpers backing the H6d sidebar splice.
 *
 * Extracted so the permission-gated insertion of the "Import students"
 * nav entry can be unit-tested without standing up the full AppShell
 * (which depends on ThemeCustomizer, `useEnabledModules`,
 * `useCatalogueReadiness`, `useTenantBrandingContext`, the ui-kit
 * Sidebar tree, motion, react-router, …). The runtime layout is
 * unchanged — `AppShell.tsx` simply calls `applyImportStudentsNavEntry`
 * with the same arguments it used inline previously.
 *
 * Keeping the helper structurally agnostic (it takes a generic
 * `TNavEntry` so callers can plug in their own `NavEntry` shape) means
 * unit tests don't need to import the full AppShell shape and the
 * helper stays trivially refactor-safe if the splice point ever moves
 * out of the settings group.
 */

/**
 * Returns whether the current user can see the H6d standing student
 * importer entry in nav. Used by both the sidebar (desktop) and the
 * mobile-overflow group so they never drift out of sync.
 *
 * `null` user (loading / anonymous) collapses to false — the nav
 * shouldn't optimistically render gated entries while the user
 * record is still resolving.
 */
export function canImportStudentsForUser(user: UserPublic | null): boolean {
  if (!user) return false;
  return userHasPermission(
    user.role,
    user.permission_overrides ?? [],
    "import_students",
    user.permission_mode ?? "additive",
  );
}

/**
 * Splice `entry` into `baseSettingsNav` immediately after its first
 * item (Catalogue) when the user holds `import_students`. The base
 * array is never mutated; callers always get a fresh array safe for
 * use as a React render key.
 *
 * When the first slot is missing (empty base nav) the splice falls
 * back to a single-entry array containing the import entry — the
 * `SETTINGS_NAV[0]!` non-null assertion that AppShell uses inline is
 * eliminated, which removes a runtime crash class if the base nav
 * is ever pruned to empty by a future refactor.
 */
export function applyImportStudentsNavEntry<TNavEntry>(
  user: UserPublic | null,
  baseSettingsNav: readonly TNavEntry[],
  entry: TNavEntry,
): TNavEntry[] {
  if (!canImportStudentsForUser(user)) {
    return [...baseSettingsNav];
  }
  if (baseSettingsNav.length === 0) {
    return [entry];
  }
  const [first, ...rest] = baseSettingsNav;
  return [first as TNavEntry, entry, ...rest];
}

/**
 * Phase F-finale-5 Slice 5a — splice the tenant-self "Forms" entry into
 * the settings group (after Catalogue) when forms editing is enabled for
 * the principal.
 *
 * The eligibility decision (`manage_forms` RBAC AND
 * `forms_editable_by_tenant` flag) lives in `useCanEditForms`; this helper
 * takes the resolved boolean so the splice stays pure + trivially
 * testable. The base array is never mutated.
 */
export function applyFormsNavEntry<TNavEntry>(
  canEditForms: boolean,
  baseSettingsNav: readonly TNavEntry[],
  entry: TNavEntry,
): TNavEntry[] {
  if (!canEditForms) {
    return [...baseSettingsNav];
  }
  if (baseSettingsNav.length === 0) {
    return [entry];
  }
  const [first, ...rest] = baseSettingsNav;
  return [first as TNavEntry, entry, ...rest];
}

/**
 * Phase F-finale-6 Slice 6b — splice the tenant-self "Admission funnel"
 * editor entry into the settings group (after Catalogue) when funnel
 * editing is enabled for the principal.
 *
 * The eligibility decision (`manage_admissions` RBAC AND
 * `admissions_editable_by_tenant` flag) lives in `useCanEditAdmissions`;
 * this helper takes the resolved boolean so the splice stays pure +
 * trivially testable. The base array is never mutated. Structurally
 * identical to `applyFormsNavEntry` — both cluster after Catalogue.
 */
export function applyAdmissionsEditorNavEntry<TNavEntry>(
  canEditAdmissions: boolean,
  baseSettingsNav: readonly TNavEntry[],
  entry: TNavEntry,
): TNavEntry[] {
  if (!canEditAdmissions) {
    return [...baseSettingsNav];
  }
  if (baseSettingsNav.length === 0) {
    return [entry];
  }
  const [first, ...rest] = baseSettingsNav;
  return [first as TNavEntry, entry, ...rest];
}

/**
 * Phase F-finale-7 Slice 7b.1 — splice the tenant-self "Seat assignment"
 * editor entry into the settings group (after Catalogue) when seating
 * editing is enabled for the principal.
 *
 * The eligibility decision combines `manage_zones` RBAC (resolved via
 * `useCanEditSeating`) AND the tenant being a `study_centre` (seat &
 * space management is a study-centre-only feature). The caller passes the
 * already-combined boolean so the splice stays pure + trivially testable.
 * The base array is never mutated. Structurally identical to
 * `applyAdmissionsEditorNavEntry` / `applyFormsNavEntry` — all cluster
 * after Catalogue.
 */
export function applySeatConfigNavEntry<TNavEntry>(
  canEditSeating: boolean,
  baseSettingsNav: readonly TNavEntry[],
  entry: TNavEntry,
): TNavEntry[] {
  if (!canEditSeating) {
    return [...baseSettingsNav];
  }
  if (baseSettingsNav.length === 0) {
    return [entry];
  }
  const [first, ...rest] = baseSettingsNav;
  return [first as TNavEntry, entry, ...rest];
}

/**
 * Phase F-finale-7 Slice 7b.4a — splice the seat-management "Zones" entry
 * into the settings group (after Catalogue) under the same gate as the
 * Slice 7b.1 "Seat assignment" entry: `manage_zones` RBAC AND the tenant
 * being a `study_centre`. The caller passes the already-combined boolean
 * (so the splice stays pure + trivially testable) and clusters this entry
 * directly after the config entry, keeping the seat-management surfaces
 * adjacent in the sidebar. The base array is never mutated.
 */
export function applySeatZonesNavEntry<TNavEntry>(
  canManageSeating: boolean,
  baseSettingsNav: readonly TNavEntry[],
  entry: TNavEntry,
): TNavEntry[] {
  if (!canManageSeating) {
    return [...baseSettingsNav];
  }
  if (baseSettingsNav.length === 0) {
    return [entry];
  }
  const [first, ...rest] = baseSettingsNav;
  return [first as TNavEntry, entry, ...rest];
}

/**
 * Phase F-finale-7 Slice 7b.4b — splice the seat-management "Seats" entry
 * into the settings group (after Catalogue) under the same gate as the
 * Slice 7b.1 "Seat assignment" and 7b.4a "Zones" entries: `manage_zones`
 * RBAC AND the tenant being a `study_centre`. Structurally identical to the
 * sibling seat-management splices; the AppShell applies it innermost (before
 * Zones and Config) so the sidebar reads config → zones → seats. The base
 * array is never mutated.
 */
export function applySeatSeatsNavEntry<TNavEntry>(
  canManageSeating: boolean,
  baseSettingsNav: readonly TNavEntry[],
  entry: TNavEntry,
): TNavEntry[] {
  if (!canManageSeating) {
    return [...baseSettingsNav];
  }
  if (baseSettingsNav.length === 0) {
    return [entry];
  }
  const [first, ...rest] = baseSettingsNav;
  return [first as TNavEntry, entry, ...rest];
}

/**
 * Phase F-finale-7 Slice 7b.4c — splice the seat-management "Allocations"
 * entry (fixed-mode permanent seat assignments) into the settings group
 * (after Catalogue) under the same gate as the sibling seat-management
 * entries: `manage_zones` RBAC AND the tenant being a `study_centre`.
 * Structurally identical to `applySeatSeatsNavEntry`; the AppShell applies
 * it innermost (before Seats, Zones and Config) so the sidebar reads
 * config → zones → seats → allocations. The base array is never mutated.
 */
export function applySeatAllocationsNavEntry<TNavEntry>(
  canManageSeating: boolean,
  baseSettingsNav: readonly TNavEntry[],
  entry: TNavEntry,
): TNavEntry[] {
  if (!canManageSeating) {
    return [...baseSettingsNav];
  }
  if (baseSettingsNav.length === 0) {
    return [entry];
  }
  const [first, ...rest] = baseSettingsNav;
  return [first as TNavEntry, entry, ...rest];
}

/**
 * Phase F-finale-7 Slice 7b.4d — splice the seat-management "Bookings" entry
 * (dynamic / zone-only per-session booking admin) into the settings group
 * (after Catalogue). Unlike its siblings this gate is `manage_bookings` (the
 * booking read/write guard) AND `study_centre`, NOT `manage_zones`: the BE
 * booking surface is guarded by `manage_bookings|book_seat`, so a
 * `manage_zones`-only principal would 403 the list. `admin` carries both by
 * default, so it still clusters with the rest of the seat surfaces. The
 * caller passes the already-combined boolean (pure + trivially testable) and
 * applies it innermost so the sidebar reads config → zones → seats →
 * allocations → bookings. The base array is never mutated.
 */
export function applySeatBookingsNavEntry<TNavEntry>(
  canManageBookingsHere: boolean,
  baseSettingsNav: readonly TNavEntry[],
  entry: TNavEntry,
): TNavEntry[] {
  if (!canManageBookingsHere) {
    return [...baseSettingsNav];
  }
  if (baseSettingsNav.length === 0) {
    return [entry];
  }
  const [first, ...rest] = baseSettingsNav;
  return [first as TNavEntry, entry, ...rest];
}

/**
 * Phase SC-22c � splice the floor-plan, learner seat-plan and live-floor
 * entries after the rest of the seat cluster. Floor plan and Seat plans
 * share the `manage_zones` gate; Live floor reads and changes bookings, so
 * it follows the `manage_bookings` gate like Bookings. Applied innermost so
 * the sidebar reads � ? bookings ? floor plan ? seat plans ? live floor.
 * The base array is never mutated.
 */
export function applySeatFloorNavEntries<TNavEntry>(
  gates: { canManageSeating: boolean; canManageBookingsHere: boolean },
  baseSettingsNav: readonly TNavEntry[],
  entries: { floorPlan: TNavEntry; seatPlans: TNavEntry; liveFloor: TNavEntry },
): TNavEntry[] {
  const added: TNavEntry[] = [];
  if (gates.canManageSeating) added.push(entries.floorPlan, entries.seatPlans);
  if (gates.canManageBookingsHere) added.push(entries.liveFloor);
  if (added.length === 0) return [...baseSettingsNav];
  if (baseSettingsNav.length === 0) return added;
  const [first, ...rest] = baseSettingsNav;
  return [first as TNavEntry, ...added, ...rest];
}
